import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  VideoLibrary as VideoIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as PendingIcon,
  AutoAwesome as GeneratingIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";

const STATUS_CONFIG = {
  pending: { color: "default", icon: <PendingIcon />, label: "Pending", bgcolor: "#f3f4f6", textColor: "#6b7280" },
  parsing: { color: "info", icon: <GeneratingIcon />, label: "Parsing", bgcolor: "#e0f2fe", textColor: "#0369a1" },
  rendering: { color: "info", icon: <GeneratingIcon />, label: "Rendering", bgcolor: "#e0f2fe", textColor: "#0369a1" },
  composing: { color: "info", icon: <GeneratingIcon />, label: "Composing", bgcolor: "#e0f2fe", textColor: "#0369a1" },
  uploading: { color: "info", icon: <GeneratingIcon />, label: "Uploading", bgcolor: "#e0f2fe", textColor: "#0369a1" },
  completed: { color: "success", icon: <CompletedIcon />, label: "Completed", bgcolor: "#dcfce7", textColor: "#166534" },
  failed: { color: "error", icon: <FailedIcon />, label: "Failed", bgcolor: "#fee2e2", textColor: "#991b1b" },
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const VideoExplanationPanel = ({ questionId, initialData, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [videoData, setVideoData] = useState(initialData || {
    has_linked_course_question: false,
    explanation_video_url: null,
    video_generation_status: null,
    video_duration_seconds: null,
    video_provider: null,
  });
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 60;

  const fetchVideoStatus = async () => {
    if (!questionId) return;
    try {
      const res = await api.get(`/admin/question-bank/${questionId}/video-status`);
      const data = res.data?.data ?? res.data;
      setVideoData(data);
      if (onStatusChange) {
        onStatusChange(data);
      }
      return data;
    } catch (err) {
      console.error("Failed to fetch video status:", err);
    }
  };

  const handleGenerateVideo = async (regenerate = false) => {
    if (!questionId) return;
    setGenerating(true);
    try {
      const res = await api.post(`/admin/question-bank/${questionId}/generate-video`, { regenerate });
      toast.success(res.data?.message || "Video generation started");
      const data = res.data?.data ?? res.data;
      setVideoData((prev) => ({
        ...prev,
        video_generation_status: data.video_generation_status || "pending",
        has_linked_course_question: true,
      }));
      if (onStatusChange) {
        onStatusChange({ ...videoData, video_generation_status: "pending" });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start video generation");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      setVideoData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (!questionId) return;

    const status = videoData?.video_generation_status;
    const isProcessing = status && ["pending", "parsing", "rendering", "composing", "uploading"].includes(status);

    if (isProcessing) {
      pollRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLLS) {
          clearInterval(pollRef.current);
          return;
        }
        const data = await fetchVideoStatus();
        if (data?.video_generation_status === "completed" || data?.video_generation_status === "failed") {
          clearInterval(pollRef.current);
          pollCountRef.current = 0;
        }
      }, 3000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollCountRef.current = 0;
      }
    };
  }, [questionId, videoData?.video_generation_status]);

  const statusConfig = STATUS_CONFIG[videoData?.video_generation_status] || STATUS_CONFIG.pending;
  const hasVideo = videoData?.explanation_video_url && videoData?.video_generation_status === "completed";
  const isProcessing = ["pending", "parsing", "rendering", "composing", "uploading"].includes(videoData?.video_generation_status);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        background: "#fafafa",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <VideoIcon sx={{ color: "#6366f1" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333" }}>
          Whiteboard Video Explanation
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Status Display */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Chip
          icon={statusConfig.icon}
          label={statusConfig.label}
          size="small"
          sx={{
            bgcolor: statusConfig.bgcolor,
            color: statusConfig.textColor,
            fontWeight: 600,
            "& .MuiChip-icon": { color: statusConfig.textColor },
          }}
        />
        {videoData?.video_duration_seconds && (
          <Typography variant="caption" sx={{ color: "#666" }}>
            Duration: {formatDuration(videoData.video_duration_seconds)}
          </Typography>
        )}
        {videoData?.video_provider && (
          <Chip label={videoData.video_provider} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
        )}
      </Box>

      {/* Progress Bar for Processing */}
      {isProcessing && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            sx={{
              borderRadius: 1,
              bgcolor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#6366f1",
              },
            }}
          />
          <Typography variant="caption" sx={{ color: "#666", mt: 0.5, display: "block" }}>
            {statusConfig.label === "Parsing" && "Analyzing question content..."}
            {statusConfig.label === "Rendering" && "Drawing whiteboard animation..."}
            {statusConfig.label === "Composing" && "Adding voiceover..."}
            {statusConfig.label === "Uploading" && "Finalizing video..."}
            {statusConfig.label === "Pending" && "Queuing video generation..."}
          </Typography>
        </Box>
      )}

      {/* Video Player */}
      {hasVideo && (
        <Box sx={{ mb: 2 }}>
          <Box
            component="video"
            controls
            src={videoData.explanation_video_url}
            sx={{
              width: "100%",
              maxWidth: 400,
              borderRadius: 1,
              backgroundColor: "#000",
            }}
          >
            Your browser does not support the video tag.
          </Box>
        </Box>
      )}

      {/* Error Message */}
      {videoData?.video_generation_status === "failed" && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "#fee2e2", borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: "#991b1b" }}>
            Video generation failed. Please try again.
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {!hasVideo && !isProcessing && (
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={() => handleGenerateVideo(false)}
            disabled={generating}
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" },
            }}
          >
            {generating ? "Starting..." : "Generate Video"}
          </Button>
        )}

        {hasVideo && (
          <>
            <Tooltip title="Regenerate video with updated content">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => handleGenerateVideo(true)}
                disabled={generating}
                sx={{ borderColor: "#6366f1", color: "#6366f1" }}
              >
                Regenerate
              </Button>
            </Tooltip>
            <Tooltip title="Check latest status">
              <IconButton onClick={fetchVideoStatus} sx={{ color: "#6366f1" }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </>
        )}

        {isProcessing && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchVideoStatus}
            disabled={loading}
            sx={{ borderColor: "#6366f1", color: "#6366f1" }}
          >
            Refresh Status
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default VideoExplanationPanel;
